/**
 * Cognito login for the agentic surface — same identity as the core web app
 * (shared user pool; the backend resolves tenant from custom:tenant_id).
 */
import { useState, type FormEvent } from 'react';
import { Bot } from 'lucide-react';
import { Button, Card, Field, Heading, Icon, Input, Stack, Text } from '@smms/design-system';
import { useAuth } from '../auth/context';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (!result.success) setError(result.error || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg p-4">
      <Card padding="lg" className="w-full max-w-sm">
        <Stack gap="md">
          <div className="flex items-center gap-2">
            <Icon as={Bot} size="md" className="text-primary" />
            <Heading level={1} className="text-lg">
              SMMS Agentic Operations
            </Heading>
          </div>
          <Text variant="body-sm" tone="muted">
            Sign in with your SMMS account. Access requires the agentic platform to be enabled for
            your organization.
          </Text>
          <form onSubmit={e => void onSubmit(e)} noValidate>
            <Stack gap="sm">
              <Field label="Email" required>
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </Field>
              <Field label="Password" required>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </Field>
              {error && (
                <Text variant="validation" role="alert">
                  {error}
                </Text>
              )}
              <Button type="submit" isLoading={submitting} className="w-full">
                Sign in
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </main>
  );
}
